import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import venueApi from '../../lib/venueApi';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  ArrowLeft, Building2, CalendarDays, MapPin, Users, Laptop, Coffee, Notebook,
  User, Mail, Loader2, Trash2, CheckCircle2, Pencil, History,
} from 'lucide-react';

function Field({ label, value, icon: Icon }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{label}</p>
        <p className="text-sm text-slate-800 mt-0.5 break-words">{value || <span className="text-slate-300">—</span>}</p>
      </div>
    </div>
  );
}

const FIELD_LABELS = {
  from_date: 'From Date',
  to_date: 'To Date',
  company_name: 'Company Name',
  lab_id: 'Lab (id)',
  lab_number: 'Lab Number',
  location_id: 'Location (id)',
  location_name: 'Location',
  total_strength: 'Total Strength',
  laptops: 'Laptops',
  lunch: 'Lunch',
  snacks: 'Snacks',
  stationery: 'Stationery',
  additional_items: 'Additional Items',
  status: 'Status',
  requester_name: 'Requester Name',
  requester_email: 'Requester Email',
  notes: 'Notes',
};

const fmtVal = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

export default function VenueRequirementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Edit dialog state
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [locations, setLocations] = useState([]);
  const [labs, setLabs] = useState([]);
  const [form, setForm] = useState(null);

  const load = () => {
    setLoading(true);
    venueApi.get(`/requirements/${id}`)
      .then((r) => setReq(r.data))
      .catch((err) => toast.error(err.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const handleToggle = async () => {
    const next = req.status === 'prospect' ? 'confirmed' : 'prospect';
    try {
      const r = await venueApi.patch(`/requirements/${id}`, { status: next });
      setReq(r.data);
      toast.success(`Marked ${next}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async () => {
    try {
      await venueApi.delete(`/requirements/${id}`);
      toast.success('Deleted');
      navigate('/venue/list');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // ─── Edit dialog ─────────────────────────────────────
  const openEdit = async () => {
    setForm({
      from_date: req.from_date,
      to_date: req.to_date,
      company_name: req.company_name,
      location_id: req.location_id,
      lab_id: req.lab_id,
      total_strength: req.total_strength || 0,
      laptops: req.laptops || 0,
      lunch: !!req.lunch,
      snacks: !!req.snacks,
      stationery: !!req.stationery,
      additional_items: req.additional_items || '',
      requester_name: req.requester_name || '',
      requester_email: req.requester_email || '',
      notes: req.notes || '',
      status: req.status,
      actor: req.requester_name || '',
    });
    setEditing(true);
    // Load masters (only if not already loaded)
    try {
      const [locR, labR] = await Promise.all([
        venueApi.get('/locations'),
        venueApi.get('/labs', { params: { location_id: req.location_id } }),
      ]);
      setLocations(locR.data.locations || []);
      setLabs(labR.data.labs || []);
    } catch { /* toast handled elsewhere */ }
  };

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!editing || !form?.location_id) return;
    venueApi.get('/labs', { params: { location_id: form.location_id } })
      .then((r) => setLabs(r.data.labs || []))
      .catch(() => {});
  }, [editing, form?.location_id]);

  const submitEdit = async () => {
    if (!form.from_date || !form.to_date) return toast.error('Both dates required');
    if (new Date(form.to_date) < new Date(form.from_date)) return toast.error('To date cannot be before From date');
    if (!form.company_name.trim()) return toast.error('Company name required');
    if (!form.lab_id) return toast.error('Lab required');
    setSavingEdit(true);
    try {
      const payload = {
        ...form,
        total_strength: Number(form.total_strength || 0),
        laptops: Number(form.laptops || 0),
      };
      const r = await venueApi.patch(`/requirements/${id}`, payload);
      setReq(r.data);
      setEditing(false);
      toast.success('Updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingEdit(false);
    }
  };

  const history = useMemo(() => {
    const arr = [...(req?.history || [])];
    arr.reverse();
    return arr;
  }, [req]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>;
  if (!req) return <div className="text-center py-16 text-slate-400 text-sm">Requirement not found.</div>;

  const supportItems = [
    req.lunch && 'Lunch',
    req.snacks && 'Snacks',
    req.stationery && 'Stationery',
  ].filter(Boolean).join(', ');

  return (
    <div className="space-y-4 max-w-4xl" data-testid="venue-detail-page">
      <Link to="/venue/list" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to list
      </Link>

      <Card className="p-6 border border-border">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge className={req.status === 'confirmed' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-yellow-100 text-yellow-800 border-yellow-200'} data-testid="venue-detail-status">
                {req.status}
              </Badge>
              <span className="text-[11px] text-slate-400">ID · {req.id.slice(0, 8)}</span>
              {history.length > 0 && (
                <span className="text-[11px] text-slate-400 flex items-center gap-1"><History className="w-3 h-3" /> {history.length} revision{history.length > 1 ? 's' : ''}</span>
              )}
            </div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{req.company_name}</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Lab {req.lab_number} · {req.location_name}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-md" onClick={openEdit} data-testid="venue-detail-edit">
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
            </Button>
            <Button variant="outline" size="sm" className="rounded-md" onClick={handleToggle} data-testid="venue-detail-toggle">
              {req.status === 'prospect' ? (<><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Confirm</>) : 'Mark Prospect'}
            </Button>
            <Button variant="outline" size="sm" className="rounded-md text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirmDelete(true)} data-testid="venue-detail-delete">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 mt-6">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Dates & Location</p>
            <Field label="From" value={req.from_date} icon={CalendarDays} />
            <Field label="To" value={req.to_date} icon={CalendarDays} />
            <Field label="Location" value={req.location_name} icon={MapPin} />
            <Field label="Lab" value={req.lab_number} icon={Building2} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Capacity & Resources</p>
            <Field label="Total Strength" value={req.total_strength || 0} icon={Users} />
            <Field label="Laptops Needed" value={req.laptops || 0} icon={Laptop} />
            <Field label="Support Items" value={supportItems || 'None'} icon={Coffee} />
            <Field label="Additional Items" value={req.additional_items} icon={Notebook} />
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-x-8">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Requester</p>
            <Field label="Name" value={req.requester_name} icon={User} />
            <Field label="Email" value={req.requester_email} icon={Mail} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Notes & Timestamps</p>
            <Field label="Notes" value={req.notes} />
            <Field label="Created" value={req.created_at ? new Date(req.created_at).toLocaleString() : '—'} />
            <Field label="Updated" value={req.updated_at ? new Date(req.updated_at).toLocaleString() : '—'} />
          </div>
        </div>
      </Card>

      {/* Audit history */}
      <Card className="p-5 border border-border" data-testid="venue-detail-history">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-4 h-4 text-violet-600" />
          <h3 className="text-sm font-semibold text-slate-900">Revision History</h3>
          <span className="text-[11px] text-slate-400">({history.length})</span>
        </div>
        {history.length === 0 ? (
          <p className="text-xs text-slate-400 py-4 text-center">No changes recorded yet. Any future edits will be logged here.</p>
        ) : (
          <ol className="relative border-l border-slate-200 ml-2 space-y-4">
            {history.map((ev, idx) => (
              <li key={idx} className="ml-4" data-testid={`venue-history-${idx}`}>
                <span className="absolute -left-1.5 flex items-center justify-center w-3 h-3 bg-violet-500 rounded-full ring-4 ring-white" />
                <div className="text-[11px] text-slate-400">
                  {new Date(ev.ts).toLocaleString()} · by <span className="text-slate-600 font-medium">{ev.actor || 'sales-portal'}</span>
                </div>
                <div className="mt-1.5 space-y-1">
                  {(ev.changes || []).map((c, ci) => (
                    <div key={ci} className="text-[12px] flex items-start gap-2 flex-wrap">
                      <span className="text-slate-500 font-medium min-w-[110px]">{FIELD_LABELS[c.field] || c.field}</span>
                      <span className="line-through text-slate-400">{fmtVal(c.from)}</span>
                      <span className="text-slate-400">→</span>
                      <span className="text-slate-800 font-medium">{fmtVal(c.to)}</span>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>

      {/* Edit dialog */}
      <Dialog open={editing} onOpenChange={(o) => !o && setEditing(false)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="venue-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Requirement</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">From Date *</Label>
                  <Input type="date" value={form.from_date} onChange={(e) => setF('from_date', e.target.value)} data-testid="edit-from-date" />
                </div>
                <div>
                  <Label className="text-xs">To Date *</Label>
                  <Input type="date" value={form.to_date} onChange={(e) => setF('to_date', e.target.value)} data-testid="edit-to-date" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Company Name *</Label>
                <Input value={form.company_name} onChange={(e) => setF('company_name', e.target.value)} data-testid="edit-company" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Location *</Label>
                  <Select value={form.location_id} onValueChange={(v) => { setF('location_id', v); setF('lab_id', ''); }}>
                    <SelectTrigger data-testid="edit-location"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Lab *</Label>
                  <Select value={form.lab_id} onValueChange={(v) => setF('lab_id', v)}>
                    <SelectTrigger data-testid="edit-lab"><SelectValue placeholder={labs.length === 0 ? 'No labs' : 'Select lab'} /></SelectTrigger>
                    <SelectContent>
                      {labs.map((l) => <SelectItem key={l.id} value={l.id}>{l.lab_number}{l.capacity ? ` · cap ${l.capacity}` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Total Strength</Label>
                  <Input type="number" min="0" value={form.total_strength} onChange={(e) => setF('total_strength', e.target.value)} data-testid="edit-strength" />
                </div>
                <div>
                  <Label className="text-xs">Laptops Needed</Label>
                  <Input type="number" min="0" value={form.laptops} onChange={(e) => setF('laptops', e.target.value)} data-testid="edit-laptops" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <label className="flex items-center gap-2 border border-slate-200 rounded p-2"><Checkbox checked={form.lunch} onCheckedChange={(v) => setF('lunch', !!v)} data-testid="edit-lunch" /> Lunch</label>
                <label className="flex items-center gap-2 border border-slate-200 rounded p-2"><Checkbox checked={form.snacks} onCheckedChange={(v) => setF('snacks', !!v)} data-testid="edit-snacks" /> Snacks</label>
                <label className="flex items-center gap-2 border border-slate-200 rounded p-2"><Checkbox checked={form.stationery} onCheckedChange={(v) => setF('stationery', !!v)} data-testid="edit-stationery" /> Stationery</label>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <div className="flex gap-2 mt-1.5">
                  {[
                    { v: 'prospect', label: 'Prospect', cls: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
                    { v: 'confirmed', label: 'Confirmed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
                  ].map((s) => (
                    <button key={s.v} type="button" onClick={() => setF('status', s.v)} data-testid={`edit-status-${s.v}`}
                      className={`text-xs px-3 py-1.5 rounded-full border font-medium transition ${form.status === s.v ? s.cls : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Additional Items / Notes</Label>
                <Textarea value={form.additional_items} onChange={(e) => setF('additional_items', e.target.value)} rows={2} data-testid="edit-additional" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Requester Name</Label>
                  <Input value={form.requester_name} onChange={(e) => setF('requester_name', e.target.value)} data-testid="edit-req-name" />
                </div>
                <div>
                  <Label className="text-xs">Requester Email</Label>
                  <Input value={form.requester_email} onChange={(e) => setF('requester_email', e.target.value)} data-testid="edit-req-email" />
                </div>
              </div>
              <div className="border-t border-slate-100 pt-3">
                <Label className="text-xs">Changed by (audit)</Label>
                <Input value={form.actor} onChange={(e) => setF('actor', e.target.value)} placeholder="Your name (for audit trail)" data-testid="edit-actor" />
                <p className="text-[10px] text-slate-400 mt-1">Optional — appears in the revision history.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={submitEdit} disabled={savingEdit} data-testid="edit-save">
              {savingEdit ? (<><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</>) : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="venue-detail-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete requirement?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the venue booking for <span className="font-medium">{req.company_name}</span>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete} data-testid="venue-detail-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
